import { computed, defineComponent, onMounted, PropType, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { CabinetAction, CabinetGetter } from '@/store/cabinet/cabinet.module';
import { Cabinet } from '@/store/cabinet/cabinet.model';
import { CabinetMinisterAction, CabinetMinisterGetter } from '@/store/cabinetMinister/cabinetMinister.module';
import { CabinetMinister } from '@/store/cabinetMinister/cabinetMinister.model';
import { ApprovementForm } from '@/store/project/project.model';
import { ApprovementSchema } from '@/store/project/project.validator';
import Datepicker from '@vuepic/vue-datepicker';

export default defineComponent({
  name: 'ApprovementFormComponent',
  components: {
    Datepicker,
  },
  props: {
    approvement: {
      type: Object as PropType<ApprovementForm>,
      required: true,
    }
  },
  emits: [
    'validated'
  ],
  setup: (props, context) => {
    const store = useStore();
    const approvementForm = reactive<ApprovementForm>({
      fiscalYearId: props.approvement.fiscalYearId,
      cabinetMinisterId: props.approvement.cabinetMinisterId,
      approvedDate: props.approvement.approvedDate,
    });
    const validator = computed(() => ApprovementSchema.validate(approvementForm, { abortEarly: false }));
    const errors = reactive({
      cabinetMinisterId: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'cabinetMinisterId')),
      approvedDate: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'approvedDate')),
    });
    const isValid = computed(() => !errors.cabinetMinisterId && !errors.approvedDate);
    const submited = ref(false);
    const cabinets = computed<Cabinet[]>(() => store.getters[CabinetGetter.BY_FISCAL_YEAR]);
    const cabinetId = ref<number | null | undefined>(null);
    const cabinetMinisters = computed<CabinetMinister[]>(() => store.getters[CabinetMinisterGetter.ALL]);
    const selectedMinister = computed<CabinetMinister | undefined>(() => cabinetMinisters.value?.find(m => m.id === approvementForm.cabinetMinisterId));
    const photoPreview = computed<string>(() => {
      return selectedMinister.value
        ? selectedMinister.value.minister.photo
        : require('@/assets/images/user.png');
    });

    // A fiscal year with a mid-year cabinet reshuffle has more than one cabinet;
    // pick the cabinet the currently-selected minister (if any) actually belongs to,
    // otherwise default to the latest cabinet in that fiscal year.
    onMounted(() => {
      store.dispatch(CabinetAction.FETCH_BY_FISCAL_YEAR, props.approvement.fiscalYearId)
        .then(() => {
          if (approvementForm.cabinetMinisterId) {
            store.dispatch(CabinetMinisterAction.FETCH_BY_FISCAL_YEAR, props.approvement.fiscalYearId)
              .then(() => {
                const current = cabinetMinisters.value.find(m => m.id === approvementForm.cabinetMinisterId);
                cabinetId.value = current ? current.cabinetId : cabinets.value[0]?.id;
                fetchCabinetMinisters();
              });
          } else {
            cabinetId.value = cabinets.value[0]?.id;
            fetchCabinetMinisters();
          }
        });
    });

    const fetchCabinetMinisters = () => {
      if (cabinetId.value) {
        store.dispatch(CabinetMinisterAction.FETCH, cabinetId.value);
      }
    };

    const cabinetChange = () => {
      approvementForm.cabinetMinisterId = null;
      fetchCabinetMinisters();
    };

    const cabinetLabel = (cabinet: Cabinet) => {
      const start = `พ.ศ. ${cabinet.startDate.getFullYear() + 543}`;
      const end = cabinet.endDate ? `พ.ศ. ${cabinet.endDate.getFullYear() + 543}` : 'ปัจจุบัน';

      return `${cabinet.name} (${start} - ${end})`;
    };

    watch(() => props.approvement, (approvement) => {
      approvementForm.fiscalYearId = approvement.fiscalYearId;
      approvementForm.cabinetMinisterId = approvement.cabinetMinisterId;
      approvementForm.approvedDate = approvement.approvedDate;
    });

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    const clearForm = () => {
      new Promise<void>((resolve) => {
        approvementForm.fiscalYearId = props.approvement.fiscalYearId;
        approvementForm.cabinetMinisterId = props.approvement.cabinetMinisterId;
        approvementForm.approvedDate = props.approvement.approvedDate;
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitForm = () => {
      submited.value = true;

      if (isValid.value) {
        context.emit('validated', approvementForm);
      }
    };

    return {
      approvementForm,
      errors,
      submited,
      photoPreview,
      cabinets,
      cabinetId,
      cabinetMinisters,
      selectedMinister,
      submitForm,
      unsubmit,
      clearForm,
      cabinetChange,
      cabinetLabel,
    };
  },
});
